import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  message,
} from 'antd';
import { PlusOutlined, PrinterOutlined, ReloadOutlined } from '@ant-design/icons';
import { apiClient } from '@/lib/api-client';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import { SmartBreadcrumbs } from '@/components/Breadcrumbs';
import type { ColumnsType } from 'antd/es/table';

const { TextArea } = Input;

type ClassificationRow = {
  id: string;
  supplier_id: string;
  supplier_code?: string;
  supplier_name?: string;
  products_services?: string;
  customer_or_legal_limited?: number | boolean;
  classification_type?: string;
  risk_severity?: number | null;
  risk_rating?: string | null;
  next_reevaluation_date?: string;
  classified_by?: string;
  classification_date?: string;
  updated_at?: string;
};

type SupplierOption = { id: string; supplier_name: string; supplier_code?: string };

function unwrapList<T>(response: unknown): T[] {
  if (Array.isArray(response)) return response as T[];
  if (response && typeof response === 'object') {
    const r = response as { data?: unknown };
    if (Array.isArray(r.data)) return r.data as T[];
    if (r.data && typeof r.data === 'object' && Array.isArray((r.data as { data?: unknown }).data)) {
      return (r.data as { data: T[] }).data;
    }
  }
  return [];
}

/** Print-friendly HTML for criticality & risk sheet */
function openCriticalityPrint(rows: ClassificationRow[]) {
  const body = rows
    .map(
      (r, i) => `<tr>
      <td>${i + 1}</td>
      <td>${r.supplier_name || ''}</td>
      <td>${(r.products_services || '').replace(/</g, '&lt;')}</td>
      <td>${r.customer_or_legal_limited ? 'Yes' : 'No'}</td>
      <td>${r.classification_type || ''}</td>
      <td>${r.risk_severity ?? ''}</td>
      <td>${r.risk_rating || ''}</td>
    </tr>`
    )
    .join('');
  const w = window.open('', '_blank');
  if (!w) return;
  w.document.write(`<!DOCTYPE html><html><head><title>Criticality & Risk Assessment</title>
    <style>
      body{font-family:Segoe UI,Arial,sans-serif;padding:24px;color:#111}
      h1{font-size:18px;margin:0 0 4px}
      .meta{font-size:12px;margin-bottom:16px}
      table{border-collapse:collapse;width:100%;font-size:12px}
      th,td{border:1px solid #333;padding:6px;vertical-align:top}
      th{background:#f0f0f0;text-align:left}
      .notes{font-size:11px;margin-top:16px}
    </style></head><body>
    <h1>Criticality &amp; Risk Assessment Sheet of Suppliers</h1>
    <div class="meta">Printed: ${new Date().toLocaleString()}</div>
    <table><thead><tr>
      <th>Sl.</th><th>Supplier Name</th><th>Supplier Scope</th>
      <th>Customer / legal / proprietary?</th><th>Criticality</th>
      <th>Risk Severity (1-5)</th><th>Risk rating</th>
    </tr></thead><tbody>${body}</tbody></table>
    <div class="notes">
      <p>Risk rating: High when severity &gt; 3 (Major/Severe); otherwise Low.</p>
      <p>Severity: 1 Minor · 2 Moderate · 3 Significant · 4 Major · 5 Severe.</p>
    </div>
    </body></html>`);
  w.document.close();
  w.focus();
  w.print();
}

export const SupplierCriticalityRiskPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<ClassificationRow[]>([]);
  const [suppliers, setSuppliers] = useState<SupplierOption[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cls, sup] = await Promise.all([
        apiClient.get('/procurement/supplier-classifications'),
        apiClient.get('/procurement/suppliers', { params: { is_active: 'true' } }),
      ]);
      setRows(unwrapList<ClassificationRow>(cls));
      setSuppliers(unwrapList<SupplierOption>(sup));
    } catch {
      message.error('Failed to load criticality & risk sheet');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const columns: ColumnsType<ClassificationRow> = [
    {
      title: 'Supplier',
      key: 'supplier',
      render: (_, r) => (
        <span>
          {r.supplier_code ? `${r.supplier_code} — ` : ''}
          {r.supplier_name || r.supplier_id}
        </span>
      ),
    },
    {
      title: 'Scope',
      dataIndex: 'products_services',
      ellipsis: true,
      width: 220,
    },
    {
      title: 'Customer / legal limited',
      key: 'cust',
      width: 140,
      render: (_, r) => (r.customer_or_legal_limited ? <Tag color="orange">Yes</Tag> : 'No'),
    },
    {
      title: 'Criticality',
      dataIndex: 'classification_type',
      width: 120,
      render: (v) => (
        <Tag color={v === 'Critical' ? 'red' : 'blue'}>{v || '—'}</Tag>
      ),
    },
    {
      title: 'Severity',
      dataIndex: 'risk_severity',
      width: 90,
      render: (v) => v ?? '—',
    },
    {
      title: 'Risk',
      dataIndex: 'risk_rating',
      width: 90,
      render: (v) =>
        v ? <Tag color={v === 'High' ? 'volcano' : 'green'}>{v}</Tag> : '—',
    },
    {
      title: 'Re-eval due',
      dataIndex: 'next_reevaluation_date',
      width: 120,
      render: (v) => (v ? String(v).slice(0, 10) : '—'),
    },
  ];

  const onSubmit = async (values: Record<string, unknown>) => {
    try {
      await apiClient.post(`/procurement/suppliers/${values.supplier_id}/classify`, {
        classification_type: values.classification_type,
        products_services: values.products_services,
        customer_or_legal_limited: Boolean(values.customer_or_legal_limited),
        risk_severity: values.risk_severity,
        justification: values.justification,
        classification_reason: values.justification,
        affects_quality_safety: values.classification_type === 'Critical',
      });
      message.success('Supplier criticality & risk saved');
      setModalOpen(false);
      form.resetFields();
      void load();
    } catch {
      message.error('Failed to classify supplier');
    }
  };

  return (
    <div>
      <SmartBreadcrumbs />
      <EnterprisePageHeader
        documentLabel="Criticality & risk assessment"
        subtitle="Classify suppliers and record risk severity (High if severity > 3)"
        primaryActions={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => void load()}>
              Refresh
            </Button>
            <Button icon={<PrinterOutlined />} onClick={() => openCriticalityPrint(rows)}>
              Print
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalOpen(true)}>
              Classify supplier
            </Button>
          </Space>
        }
      />
      <WorkspaceLayout>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="QMS overlay — does not change PO/PR/GRN logic"
          description="Re-evaluation months follow the risk × performance frequency table when risk and annual scorecards exist; otherwise Critical=24 months / Non-Critical=12 months."
        />
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={rows}
          size="small"
          pagination={{ pageSize: 25 }}
          scroll={{ x: 900 }}
        />
      </WorkspaceLayout>

      <Modal
        title="Classify supplier"
        open={modalOpen}
        onCancel={() => {
          setModalOpen(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        destroyOnHidden
        width={640}
      >
        <Form form={form} layout="vertical" onFinish={onSubmit}>
          <Form.Item
            name="supplier_id"
            label="Supplier"
            rules={[{ required: true, message: 'Select supplier' }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              options={suppliers.map((s) => ({
                value: s.id,
                label: s.supplier_code ? `${s.supplier_code} — ${s.supplier_name}` : s.supplier_name,
              }))}
            />
          </Form.Item>
          <Form.Item
            name="products_services"
            label="Supplier scope (products / services)"
            rules={[{ required: true, message: 'Enter scope' }]}
          >
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item
            name="customer_or_legal_limited"
            label="Customer-specified or limited by legal / proprietary?"
            valuePropName="checked"
            initialValue={false}
          >
            <Switch checkedChildren="Yes" unCheckedChildren="No" />
          </Form.Item>
          <Form.Item
            name="classification_type"
            label="Criticality category"
            rules={[{ required: true }]}
          >
            <Radio.Group>
              <Radio value="Critical">Critical</Radio>
              <Radio value="Non-Critical">Non-Critical</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            name="risk_severity"
            label="Risk severity rating (1–5)"
            rules={[{ required: true, message: 'Select severity' }]}
            extra="1 Minor · 2 Moderate · 3 Significant · 4 Major · 5 Severe. High risk when > 3."
          >
            <InputNumber min={1} max={5} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="justification" label="Notes / justification">
            <TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SupplierCriticalityRiskPage;
